const REQUIRED_METHODS = Object.freeze([
  "supportsActivity",
  "getDomainId",
  "validateConfiguration",
  "createSession",
  "getCatalog",
  "getNavigation",
  "getAvailableActions",
  "getWorkspaceDescriptor",
  "resolveComponent",
  "dispose",
]);


export class POSDomainAdapterContractError extends Error {
  constructor(message, { issues = [] } = {}) {
    super(message);

    this.name = "POSDomainAdapterContractError";

    this.issues = Object.freeze(
      Array.isArray(issues)
        ? issues.map((issue) =>
            Object.freeze({
              ...issue,
            })
          )
        : []
    );
  }
}


/**
 * Assert the structural contract of a POSDomainAdapter.
 *
 * This assertion validates only the generic public contract.
 *
 * Domain-specific validation remains owned by the concrete adapter,
 * notably through supportsActivity() and validateConfiguration().
 *
 * The generic contract performs no:
 * - accounting write;
 * - payment;
 * - NF525 certification;
 * - inventory mutation;
 * - receipt creation;
 * - implicit persistence.
 */
export function assertPOSDomainAdapter(adapter) {
  const issues = collectIssues(adapter);

  if (issues.length > 0) {
    throw new POSDomainAdapterContractError(
      "POSDomainAdapter contract validation failed.",
      {
        issues,
      }
    );
  }

  return adapter;
}


/**
 * Return whether a value satisfies the structural
 * POSDomainAdapter contract.
 *
 * This predicate never throws for an invalid candidate.
 */
export function isPOSDomainAdapter(adapter) {
  return collectIssues(adapter).length === 0;
}


/**
 * Create an immutable POSDomainAdapter definition.
 *
 * The public adapter definition is immutable.
 *
 * Any domain-private state must remain inside closures owned by
 * the concrete implementation and must not be stored as mutable
 * public contract state.
 */
export function createPOSDomainAdapter(definition) {
  assertPOSDomainAdapter(definition);

  return Object.freeze({
    ...definition,
  });
}


function collectIssues(adapter) {
  if (!isPlainObject(adapter)) {
    return [
      createIssue(
        "$",
        "invalid_type",
        "POSDomainAdapter must be a plain object."
      ),
    ];
  }

  const issues = [];

  for (const methodName of REQUIRED_METHODS) {
    if (typeof adapter[methodName] !== "function") {
      issues.push(
        createIssue(
          methodName,
          "missing_method",
          `POSDomainAdapter requires ${methodName}().`
        )
      );
    }
  }

  return issues;
}


function createIssue(path, code, message) {
  return Object.freeze({
    path,
    code,
    message,
  });
}


function isPlainObject(value) {
  if (
    value === null ||
    typeof value !== "object" ||
    Array.isArray(value)
  ) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);

  return (
    prototype === Object.prototype ||
    prototype === null
  );
}
